import React, {useState, useRef, useEffect} from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { DropDownField, TextInputField, CalendarField, CheckboxField } from "../../../helpers";
import Button from "../../../helpers/Button";
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';

import { fetchInvoiceVerificationData, updateSearchParams, updateSearchOptions } from "../../../services/redux/reducers/invoiceVerification/invoiceVerification";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { getAddFilterOptions, getDateRange } from "../../../utils";
import DateFilter from "../../../common/DateFilter";


const invoiceVerificationSearchConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        disabled: false,
        options: [
            { label: 'Invoice Verification Data', value: 'invoiceVerificationData' }
        ]
    },
    {
        srNO: 2,
        static: true,
        type: "SEARCH_BY",
        name: "searchBy",
        label: "Search by",
        disabled: false,
        options: [
            { label: 'No Filters', value: 'all' },
            { label: 'Company Name', value: 'companyName' },
            { label: 'Seller PI No.', value: 'sellerPINo' },
            { label: 'Seller PO No.', value: 'sellerPODocNo' },
            { label: 'KAM Name', value: 'kamName' },
            { label: 'Kam Email', value: 'kamEmail' },
            { label: 'GSTIN', value: 'gstinCode' },
            { label: 'Booked By', value: 'bookedBy' }
        ]
    },
    {
        srNO: 3,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        disabled: false,
        options: [
            { label: 'PI Uploaded Date', value: 'piUploadedDate' },
            { label: 'PI Created Date', value: 'createdAt' },
            { label: 'Purchase Invoice Date', value: 'sellerPIDate' },
            { label: 'SellerPO Created Date', value: 'sellerPODate' },
            { label: 'PI Booked Date', value: 'piBookedDate' },
        ]
    },
    {
        srNO: 4,
        static: false,
        type: "SELECT",
        name: "bookedStatus",
        label: "Booking Status",
        disabled: false,
        options: [
            { label: 'Booked', value: true },
            { label: 'UnBooked', value: false }
        ]
    },
    {
        srNO: 5,
        static: false,
        type: "SELECT",
        name: "pmBranchId",
        label: "Branch",
        disabled: false
    },
    {
        srNO: 6,
        static: false,
        type: "SELECT",
        name: "POType",
        label: "PO Type",
        disabled: false,
        options: [
            { label: 'NORMAL', value: 'NORMAL' },
            { label: 'EXPENSE', value: 'EXPENSE' }
        ]
    },
    {
        srNO: 7,
        static: false,
        type: "SELECT",
        name: "expenseCategoryId",
        label: "Expense Category",
        disabled: false
    }
]

const PIInvoiceBookingSearch = (props) => {
    const {expenseCategories} = props

    const dispatch = useDispatch()
    const [searchConfig, setSearchConfig] = useState(invoiceVerificationSearchConfig)

    const isLoading = useSelector(state => state.invoiceVerification.loading)
    const paginationParams = useSelector(state => state.invoiceVerification.paginationParams)
    const searchParams = useSelector(state => state.invoiceVerification.searchParams)
    const searchOptions = useSelector(state => state.invoiceVerification.searchOptions)

    const filterMenu = useRef(null)

    const userDetails = useSelector((state) => state.userDetails)
    const companyBranches = userDetails.PMCompanyDetails.companyBranches

    useEffect(()=> {
        const currentDate = new Date()
        let fromDate  = currentDate.setMonth(currentDate.getMonth() -  1)
        fromDate = new Date(fromDate)
        let toDate = new Date()
        const updatedSearchParams = {...searchParams, fromDate: fromDate, toDate: toDate }
        dispatch(updateSearchParams({ ...updatedSearchParams}))

        if(searchOptions.length === 0){
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    const addFilterOption = (srNO) => {
        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        if(relatedConfig.name === "pmBranchId"){
            const updatedCompanyBranches = companyBranches.map(eachCompanyBranch => ({...eachCompanyBranch, label: eachCompanyBranch.branchName, value: eachCompanyBranch.branchId}))
            relatedConfig.options = updatedCompanyBranches
        }
        if(relatedConfig.name === "expenseCategoryId"){
            const updatedExpenseCategories = expenseCategories.map(eachCategory => ({...eachCategory, label: eachCategory.expenseCategoryName, value: eachCategory.expenseCategoryId}))
            relatedConfig.options = updatedExpenseCategories
        }
        dispatch(updateSearchOptions([...searchOptions, relatedConfig]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = {...searchParams}

        if(relatedSearchOption.type === "SEARCH_BY"){
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "DATE"){
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            console.log("updateSearchParams", updatedSearchParams)
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "SELECT"){
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleCheckboxSelect = (event) => {
        const updatedSearchParams = {
            ...searchParams,
            [event.target.name]: event.target.checked
        }
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleSearchOnChange = (event) => {
        if(["fromDate", "toDate"].includes(event.target.name)){
            let eventValue = event.target.value

            const updatedSearchParams = {...searchParams, [event.target.name]: eventValue}

            if (eventValue instanceof Date) {
                if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                    updatedSearchParams.range = null
                }
    
                if (event.target.name === "toDate") {
                    eventValue.setHours(23, 59, 0, 0)
                }
                if (event.target.name === "fromDate") {
                    eventValue.setHours(0, 0, 0, 0)
                }
                eventValue = eventValue.toISOString()
    
                updatedSearchParams[event.target.name] = eventValue
            }

            dispatch(updateSearchParams(updatedSearchParams))
        } 
        else if(["searchBy"].includes(event.target.name)){
            const updatedSearchParams = {...searchParams, [event.target.name]: event.target.value}
            
            const updatedSearchOptions = searchOptions.map(eachOption => {
                const updatedOption = {...eachOption}
                if(updatedOption.name === "dateType"){
                    updatedOption.disabled = ["sellerPINo", "sellerPODocNo"].includes(event.target.value) ? true : false
                }
                return updatedOption
            })
            dispatch(updateSearchOptions([...updatedSearchOptions]))

            dispatch(updateSearchParams(updatedSearchParams))
        }
        else {
            const updatedSearchParams = {...searchParams, [event.target.name]: event.target.value}
            dispatch(updateSearchParams(updatedSearchParams))
        }
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        getDateRange(range)
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSearch = () => {
        if(!searchParams.searchBy){
            return Toast.current.show({ severity: 'warn', detail: "Please enter search by", life: 3000 })
        }
        if(!searchParams.searchText && searchParams.searchBy != 'all'){
            return Toast.current.show({ severity: 'warn', detail: "Please enter search text", life: 3000 })
        }

        const piWiseInvoiceParams = {
            ...paginationParams, 
            ...searchParams,
            smartSearch: false
        }

        if(["all", "bookedBy"].includes(searchParams.searchBy)){
            if(!searchParams.fromDate || !searchParams.toDate){
                return dispatch(raiseToast({severity:'error', detail: "Please select from date and to date" , life: 3000}))
            }
            let from_date = searchParams.fromDate
            let to_date = searchParams.toDate
            if (searchParams.fromDate) {
                from_date = moment(searchParams.fromDate, "DD.MM.YYYY").toDate()
            }
            if (searchParams.toDate) {
                to_date = moment(searchParams.toDate, "DD.MM.YYYY").toDate()
            }
            const date_difference =  moment(new Date(to_date)).diff(moment(new Date(from_date)), 'days')

            if(date_difference > 93){
                return dispatch(raiseToast({severity:'error', detail: "Please select Date range within 3 month" , life: 3000}))
            }
        } else {
            piWiseInvoiceParams.dateType = null
        }
        
        dispatch(fetchInvoiceVerificationData(piWiseInvoiceParams))
        const updatedSeachParams = {
            ...searchParams,
            smartSearch: false
        }
        dispatch(updateSearchParams(updatedSeachParams))
    }

    return(
        <div className="p-card" style={{border:"1px solid #e9ecef"}} onKeyDown={handleSearchKeyPress}>
            <Toast ref={Toast} />
            <div className="grid"> 
                {
                    searchOptions.map(eachOption => {
                        if(eachOption.type === "SEARCH_BY"){
                            return (
                                <>  
                                    { !eachOption.static ? 
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={()=> removeFilterOption(eachOption.srNO)}></i>
                                        </div>
                                        :
                                        null 
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name="searchBy" labelText="Search By" options={eachOption.options} value={searchParams["searchBy"]} onChange={handleSearchOnChange} />
                                    </div>
                                    { searchParams.searchBy !== "all" ?
                                        <div className="flex flex-column col-12">
                                            <TextInputField name="searchText" labelText="Search Text" value={searchParams["searchText"]} onChange={handleSearchOnChange} />
                                        </div> : null
                                    }
                                </>
                            )
                        }
                        if(eachOption.type === "DATE"){
                            return (
                                <>  
                                    <DateFilter 
                                        eachOption={eachOption} 
                                        searchParams={searchParams} 
                                        dateOptions={["MTD", "PM", "L15", "L30"]}
                                        handleDateRangeChange={handleDateRangeChange} 
                                        handleSearchOnChange={handleSearchOnChange}
                                        removeFilterOption={removeFilterOption}
                                    />
                                </>
                            )
                        }
                        if(eachOption.type === "SELECT"){
                            return (
                                <>
                                    { !eachOption.static ? 
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={()=> removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null 
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={eachOption.options} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }
                    })
                }
                <div className="flex flex-column col-12">
                    <div className="flex flex-row">
                        <CheckboxField name="openIssues" onChange={handleCheckboxSelect} checked={searchParams.openIssues} label="Open Issues" />
                    </div>
                </div>
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{cursor : isLoading? 'not-allowed' : 'pointer'}}>
                            <Button label="Search" style={{flexGrow: 1}} className="py-2" onClick={handleSearch} disabled={isLoading}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PIInvoiceBookingSearch;